import React, { useCallback, useEffect, useState, useContext } from 'react';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './StudentMetricsDashboard.module.scss';
import PageLoader from '@/components/school-management/PageLoader/PageLoader';
import ChartsWrapper from './ChartsWrapper/ChartsWrapper';
import Statistics from './Statistics/Statistics';
import AvgStudentGrowthChar from './AvgStudentGrowthChar/AvgStudentGrowthChar';
import AvgStudentProgressAtCurrentLevelChar from './AvgStudentProgressAtCurrentLevelChar/AvgStudentProgressAtCurrentLevelChar';
import StudentLevelProgressionChar from './StudentLevelProgressionChar/StudentLevelProgressionChar';
import AvgScoresChar from './AvgScoresChar/AvgScoresChar';
import StudentsAcrossSkillsChar from './StudentsAcrossSkillsChar/StudentsAcrossSkillsChar';
import StudentsAcrossSkillsCharTools from './StudentsAcrossSkillsCharTools/StudentsAcrossSkillsCharTools';
import AvgStudentProgressAtCurrentLevelDrillDown from './AvgStudentProgressAtCurrentLevelDrillDown/AvgStudentProgressAtCurrentLevelDrillDown';
import AvgScoresDrillDown from './AvgScoresDrillDown/AvgScoresDrillDown';
import StudentMetricsDashboardTools from './StudentMetricsDashboardTools/StudentMetricsDashboardTools';
import { reportService } from '@/school-management/services/report';
import { studentService } from '@/school-management/services/student';
import {
  StudentMetricsDashboardTimeRange,
  StudentMetricsDashboardContent,
  StudentMetricsDashboardFilters,
} from '@/school-management/types/generate-report';
import { ProcessingContext } from '@/school-management/context/ProcessingContext';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

const emptyData: StudentMetricsDashboardContent = {
  totalStudents: 0,
};

type DrillDownType =
  | ''
  | 'AvgStudentGrowth'
  | 'AvgStudentProgressAtCurrentLevel'
  | 'StudentLevelProgression'
  | 'AvgScores'
  | 'StudentsAcrossSkills';

interface StudentMetricsDashboardProps {
  filters: StudentMetricsDashboardFilters;
  drillDownType: DrillDownType;
  setDrillDownType: (type: DrillDownType) => void;
  toolsContainer?: any;
  className?: string;
  itemClassName?: string;
  statisticsClassName?: string;
}

const StudentMetricsDashboard: React.FC<StudentMetricsDashboardProps> = ({
  filters,
  drillDownType,
  setDrillDownType,
  toolsContainer,
  className,
  itemClassName,
  statisticsClassName,
}) => {
  const { t } = useAdminTranslation();
  const processing = useContext(ProcessingContext);
  const { user } = useAppSelector(authSelector);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [emptyState, setEmptyState] = useState<'' | 'global' | 'filter' | 'noStudents'>('');
  const [data, setData] = useState<StudentMetricsDashboardContent | undefined>(emptyData);

  useEffect(() => {
    let isActive: boolean = true;

    setIsLoading(true);

    const initData = async () => {
      setIsLoading(true);

      let data: StudentMetricsDashboardContent = emptyData;

      try {
        const response = (await reportService.fetchStudentMetricsDashboard(filters))?.data?.data;

        if (response?.totalStudents) {
          data = response;
        }
      } catch (er) {}

      if (!isActive) {
        return;
      }

      const hasFilterData =
        (!!filters.timeRange && filters.timeRange !== StudentMetricsDashboardTimeRange.all_time) ||
        (filters.gradeIds && filters.gradeIds.length > 0) ||
        (filters.groupIds && filters.groupIds.length > 0) ||
        (filters.levelIds && filters.levelIds.length > 0) ||
        (filters.teacherIds && filters.teacherIds.length > 0);

      if (data.totalStudents > 0) {
        setEmptyState('');
      } else if (hasFilterData) {
        setEmptyState('filter');
      } else {
        const schoolId = user?.metadata?.organizationDetails?.organizationId || '';
        let schoolHasStudents = false;
        try {
          const studentsResponse = await studentService.getAllStudents(schoolId, { pageSize: 1 });
          schoolHasStudents = (studentsResponse?.data?.totalCount ?? 0) > 0;
        } catch {}
        if (!isActive) {
          return;
        }
        setEmptyState(schoolHasStudents ? 'global' : 'noStudents');
      }
      setData(data);
      setIsLoading(false);
    };

    const timer = setTimeout(() => {
      if (!drillDownType) {
        initData();
      }
    }, 100);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [filters, drillDownType]);

  const onExportData = useCallback(() => {
    if (!processing || !data) {
      return;
    }

    const schoolId = user?.metadata?.organizationDetails?.organizationId || '';
    processing?.exportStudentMetricsDashboard(t, data, filters, schoolId, user);
  }, [t, processing, data, filters, user]);

  if (
    drillDownType === 'AvgStudentGrowth' ||
    drillDownType === 'AvgStudentProgressAtCurrentLevel' ||
    drillDownType === 'StudentLevelProgression'
  ) {
    return (
      <div className={styles.drillDown}>
        <AvgStudentProgressAtCurrentLevelDrillDown
          filters={filters}
          drillDownType={drillDownType}
          toolsContainer={toolsContainer}
          isShowLevelHint={drillDownType !== 'AvgStudentProgressAtCurrentLevel'}
        />
      </div>
    );
  }
  if (drillDownType === 'AvgScores' || drillDownType === 'StudentsAcrossSkills') {
    return (
      <div className={styles.drillDown}>
        <AvgScoresDrillDown
          filters={filters}
          drillDownType={drillDownType}
          toolsContainer={toolsContainer}
        />
      </div>
    );
  }

  if (isLoading) {
    return <PageLoader title={t('studentMetricsDashboard.loadingMetrics') || ''} />;
  }

  if (emptyState) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateTitle}>
          {t(`studentMetricsDashboard.emptyState.${emptyState}.title`)}
        </div>
        <div className={styles.emptyStateDescription}>
          {t(`studentMetricsDashboard.emptyState.${emptyState}.description`)}
        </div>
      </div>
    );
  }

  const dashboardItemClassName = classes(styles.dashboardItem, {
    [itemClassName || '']: !!itemClassName,
  });

  return data ? (
    <div
      className={classes(styles.dashboard, {
        [className || '']: !!className,
      })}
    >
      <StudentMetricsDashboardTools
        toolsContainer={toolsContainer}
        totalStudents={data?.totalStudents}
        onExportData={onExportData}
      />
      <div className={dashboardItemClassName}>
        <Statistics
          className={statisticsClassName}
          data={data?.kpis}
          totalStudents={data?.totalStudents || 0}
        />
      </div>
      <div className={dashboardItemClassName}>
        <ChartsWrapper
          title={t('studentMetricsDashboard.charts.avgStudentGrowth.title') || ''}
          subTitle={t('studentMetricsDashboard.charts.avgStudentGrowth.subTitle') || ''}
          onSelect={() => {
            setDrillDownType('AvgStudentGrowth');
          }}
        >
          <AvgStudentGrowthChar data={data?.charts?.averageStudentGrowth} />
        </ChartsWrapper>
      </div>
      <div className={dashboardItemClassName}>
        <ChartsWrapper
          title={t('studentMetricsDashboard.charts.avgStudentProgressAtCurrentLevel.title') || ''}
          subTitle={
            t('studentMetricsDashboard.charts.avgStudentProgressAtCurrentLevel.subTitle') || ''
          }
          onSelect={() => {
            setDrillDownType('AvgStudentProgressAtCurrentLevel');
          }}
        >
          <AvgStudentProgressAtCurrentLevelChar
            data={data?.charts?.studentProgressAtCurrentLevel}
          />
        </ChartsWrapper>
      </div>
      <div className={dashboardItemClassName}>
        <ChartsWrapper
          title={t('studentMetricsDashboard.charts.studentLevelProgression.title') || ''}
          subTitle={t('studentMetricsDashboard.charts.studentLevelProgression.subTitle') || ''}
          onSelect={() => {
            setDrillDownType('StudentLevelProgression');
          }}
        >
          <StudentLevelProgressionChar data={data?.charts?.studentLevelTransitions} />
        </ChartsWrapper>
      </div>
      <div className={dashboardItemClassName}>
        <ChartsWrapper
          title={t('studentMetricsDashboard.charts.avgScores.title') || ''}
          subTitle={t('studentMetricsDashboard.charts.avgScores.subTitle') || ''}
          onSelect={() => {
            setDrillDownType('AvgScores');
          }}
        >
          <AvgScoresChar data={data?.charts?.averageScoresPerSkill} />
        </ChartsWrapper>
      </div>
      <div className={dashboardItemClassName}>
        <ChartsWrapper
          title={t('studentMetricsDashboard.charts.studentsAcrossSkills.title') || ''}
          subTitle={t('studentMetricsDashboard.charts.studentsAcrossSkills.subTitle') || ''}
          tools={<StudentsAcrossSkillsCharTools />}
          onSelect={() => {
            setDrillDownType('StudentsAcrossSkills');
          }}
        >
          <StudentsAcrossSkillsChar data={data?.charts?.studentsAcrossSkills} />
        </ChartsWrapper>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default StudentMetricsDashboard;
